<template>
  <v-dialog 
    v-model="dialog" 
    :width="dialogWidth" 
    persistent
    transition="dialog-bottom-transition"
    class="reactions-dialog"
    overlay-opacity="0.7"
  >
    <v-card class="reactions-card">
      <!-- Ultra Modern Header with Glassmorphism -->
      <div class="reactions-header">
        <div class="header-background"></div>
        <div class="header-content">
          <div class="header-left">
            <div class="reactions-icon-container">
              <div class="floating-reactions">
                <div class="reaction-float" v-for="n in 6" :key="n">
                  <v-icon>mdi-heart</v-icon>
                </div>
              </div>
              <v-icon class="main-icon" size="28">mdi-heart-multiple</v-icon>
            </div>
            <div class="header-text">
              <h1 class="title-modern">{{ $t("reactions") }}</h1>
              <div class="subtitle-modern" v-if="totalReactions > 0">
                {{ totalReactions }} {{ totalReactions === 1 ? 'person' : 'people' }} reacted
              </div>
            </div>
          </div>
          <v-btn 
            icon 
            @click="onCloseDialog" 
            class="close-btn-modern"
            x-large
          >
            <v-icon size="24">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Ultra Modern Tabs with Floating Design -->
      <div class="tabs-container" v-if="reactions.length">
        <div class="tabs-wrapper">
          <div class="tab-slider" :style="sliderStyle"></div>
          <div 
            v-for="(title, index) in titles" 
            :key="index"
            class="modern-tab"
            :class="{ 'active': tab === index }"
            @click="tab = index"
          >
            <div class="tab-icon-wrapper">
              <div v-if="title === 'All'" class="all-reactions-icon">
                <div class="icon-stack">
                  <v-icon size="16" class="icon-1">mdi-heart</v-icon>
                  <v-icon size="14" class="icon-2">mdi-thumb-up</v-icon>
                  <v-icon size="12" class="icon-3">mdi-emoticon-happy</v-icon>
                </div>
              </div>
              <div v-else class="reaction-icon-modern">
                <v-img
                  :src="require(`@/assets/svg/reactions/circle/${title.toLowerCase()}.svg`)"
                  width="26"
                  height="26"
                  contain
                />
              </div>
            </div>
            <span class="tab-label">{{ title === 'All' ? $t('all') : title }}</span>
            <div class="tab-count-modern">{{ reactions[index].length }}</div>
          </div>
        </div>
      </div>

      <!-- Revolutionary Content Area -->
      <div class="content-area" v-if="reactions.length">
        <div class="content-wrapper">
          <transition-group 
            name="reaction-list" 
            tag="div" 
            class="reactions-grid"
            @before-enter="beforeEnter"
            @enter="enter"
          >
            <div 
              v-for="(item, index) in currentReactions" 
              :key="item._id"
              class="reaction-card-modern"
              :data-index="index"
              @click="goToProfile(item._id, item.poster_type)"
            >
              <!-- Hover Glow Effect -->
              <div class="card-glow"></div>
              
              <!-- Avatar with 3D Effect -->
              <div class="avatar-section">
                <div class="avatar-container-3d">
                  <editable-avatar
                    :image="item.poster_image || placeholderImage "
                    :editable="false"
                    :size="52"
                    :shadow="false"
                    class="user-avatar-3d"
                  />
                  <div class="avatar-ring"></div>
                </div>
                <div class="reaction-badge-floating">
                  <div class="badge-glow"></div>
                  <v-img
                    :src="require(`@/assets/svg/reactions/circle/${item.type.toLowerCase()}.svg`)"
                    width="22"
                    height="22"
                    contain
                  />
                </div>
              </div>

              <!-- User Info with Modern Typography -->
              <div class="user-section">
                <div class="user-name-modern">
                  <span v-if="item.poster_type === 'Agency'">
                    {{ truncateName(item.poster_name) || "Deleted User" }}
                  </span>
                  <span v-else>
                    {{ item.first_name && item.last_name ? truncateFullName(item.first_name, item.last_name) : "Deleted User" }}
                  </span>
                </div>
                <div class="user-type-modern">
                  <div 
                    class="type-indicator"
                    :class="`type-${item.poster_type.toLowerCase()}`"
                  ></div>
                  {{ item.poster_type }}
                </div>
              </div>

              <!-- Interactive Arrow -->
              <div class="arrow-section">
                <div class="arrow-circle">
                  <v-icon size="18" color="#ffff">mdi-arrow-right</v-icon>
                </div>
              </div>
            </div>
          </transition-group>
        </div>
      </div>

      <!-- Futuristic Empty State -->
      <div v-else-if="!loading" class="empty-state-modern">
        <div class="empty-animation">
          <div class="empty-circle" v-for="n in 3" :key="n"></div>
        </div>
        <div class="empty-content">
          <h2 class="empty-title">No reactions yet</h2>
          <p class="empty-subtitle">Be the first to show some love!</p>
        </div>
      </div>

      <!-- Premium Loading Animation -->
      <div v-if="loading" class="loading-modern">
        <div class="loading-spinner">
          <div class="spinner-ring" v-for="n in 3" :key="n"></div>
        </div>
        <p class="loading-text-modern">Loading reactions...</p>
      </div>

      <!-- Modern Snackbar -->
      <v-snackbar 
        v-model="snackbar"
        :timeout="4000"
        class="snackbar-modern"
        bottom
        rounded="pill"
      >
        <div class="snackbar-content">
          <v-icon left color="white">mdi-alert-circle</v-icon>
          <span>{{ errorMessage }}</span>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn
            text
            v-bind="attrs"
            @click="snackbar = false"
            class="snackbar-action"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EditableAvatar from "../../components/EditableAvatar.vue";

export default {
  components: { 
    EditableAvatar
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onCloseDialog: {
      type: Function
    },
    _id: String,
        postType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      errorMessage: null,
      snackbar: false,
      tab: 0,
      reactions: [],
      titles: [],
       placeholderImage: require("../../assets/default.jpg"),
      reactionTypes: [
        "Like",
        "Celebrate", 
        "Support",
        "Love",
        "Insightful",
        "Curious"
      ]
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    dialogWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs': return '96vw'
        case 'sm': return '85vw'
        case 'md': return '720px'
        default: return '840px'
      }
    },
    totalReactions() {
      return this.reactions.length > 0 ? this.reactions[0].length : 0;
    },
    currentReactions() {
      return this.reactions[this.tab] || [];
    },
    sliderStyle() {
      if (!this.titles.length) return {};
      const width = 100 / this.titles.length;
      const left = width * this.tab;
      return {
        width: `${width}%`,
        left: `${left}%`
      };
    }
  },
  methods: {
    ...mapActions({
      getEventReactions: "event/getCommentReactions",
      getBlogReactions: "blog/getCommentReactions"
    }),
      getReactions() {
      // Use the postType prop to determine which action to call
      if (this.postType === "blog") {
        return this.getBlogReactions({ _id: this._id });
      } else {
        return this.getEventReactions({ _id: this._id });
      }
    },
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },
    filterData() {
      this.titles = ["All"];
      for (var tI = 0; tI < this.reactionTypes.length; tI++) {
        var ok = false;
        var array = [];
        for (var i = 0; i < this.reactions[0].length; i++) {
          if (this.reactions[0][i].type == this.reactionTypes[tI]) {
            ok = true;
            array.push(this.reactions[0][i]);
          }
        }
        if (ok) {
          this.titles.push(this.reactionTypes[tI]);
          this.reactions.push(array);
        }
      }
    },
    goToProfile(poster, type) {
      if (type == "Agency") {
        let routeData = this.$router.resolve({
          name: "agency-profile-events",
          params: { id: poster }
        });
        window.open(routeData.href, "_blank");
      } else if (type == "Volunteer") {
        let routeData = this.$router.resolve({
          name: "volunteer-profile-events", 
          params: { id: poster }
        });
        window.open(routeData.href, "_blank");
      }
    },
    truncateFullName(first, last, length = 28) {
      const fullName = `${first || ''} ${last || ''}`.trim();
      return fullName.length > length ? fullName.slice(0, length) + '...' : fullName;
    },
    truncateName(name, length = 28) {
      if (!name) return '';
      return name.length > length ? name.slice(0, length) + '...' : name;
    },
    beforeEnter(el) {
      el.style.opacity = 0;
      el.style.transform = 'translateY(30px) scale(0.95)';
    },
    enter(el, done) {
      const delay = el.dataset.index * 50;
      setTimeout(() => {
        el.style.transition = 'all 0.6s cubic-bezier(0.23, 1, 0.320, 1)';
        el.style.opacity = 1;
        el.style.transform = 'translateY(0) scale(1)';
        done();
      }, delay);
    }
  },
  mounted() {
    this.reactions = [];
    this.loading = true;
    this.getReactions()
      .then(res => {
        this.loading = false;
        this.reactions.push(res);
        this.filterData();
      })
      .catch(error => {
        this.loading = false;
        this.showErrorMessage(error.response?.data?.message || error.message);
      });
  }
};
</script>

<style scoped>
/* Global Dialog Styling */
.reactions-dialog :deep(.v-overlay__content) {
  max-height: 95vh !important;
}

.reactions-dialog :deep(.v-dialog) {
  border-radius: 24px !important;
  overflow: hidden;
  backdrop-filter: blur(20px);
  box-shadow: 
    0 32px 64px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
}

.reactions-card {
  border-radius: 24px !important;
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  max-height: 95vh;
  overflow: hidden;
  position: relative;
}

/* Ultra Modern Header */
.reactions-header {
  position: relative;
  padding: 32px;
  background: linear-gradient(135deg, #084468 0%, #1da1f2 50%, #3b7ca1 100%);
  overflow: hidden;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  animation: backgroundMove 20s ease-in-out infinite;
}

@keyframes backgroundMove {
  0%, 100% { transform: translateX(0) translateY(0); }
  25% { transform: translateX(-20px) translateY(-10px); }
  50% { transform: translateX(20px) translateY(-20px); }
  75% { transform: translateX(-10px) translateY(10px); }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.reactions-icon-container {
  position: relative;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.floating-reactions {
  position: absolute;
  width: 100%;
  height: 100%;
}

.reaction-float {
  position: absolute;
  color: rgba(255, 255, 255, 0.3);
  animation: float 3s ease-in-out infinite;
}

.reaction-float:nth-child(1) { top: 10%; left: 20%; animation-delay: 0s; }
.reaction-float:nth-child(2) { top: 70%; right: 15%; animation-delay: 0.5s; }
.reaction-float:nth-child(3) { bottom: 20%; left: 60%; animation-delay: 1s; }
.reaction-float:nth-child(4) { top: 40%; right: 40%; animation-delay: 1.5s; }
.reaction-float:nth-child(5) { bottom: 60%; left: 10%; animation-delay: 2s; }
.reaction-float:nth-child(6) { top: 20%; left: 70%; animation-delay: 2.5s; }

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg) scale(1); opacity: 0.3; }
  50% { transform: translateY(-10px) rotate(180deg) scale(1.2); opacity: 0.6; }
}

.main-icon {
  color: white;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: iconPulse 2s ease-in-out infinite alternate;
}

@keyframes iconPulse {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4); }
  100% { transform: scale(1.05); box-shadow: 0 0 20px 10px rgba(255, 255, 255, 0); }
}

.header-text {
  color: white;
}

.title-modern {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  background: linear-gradient(45deg, #ffffff, #f0f8ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle-modern {
  font-size: 0.95rem;
  opacity: 0.9;
  margin-top: 4px;
  font-weight: 400;
}

.close-btn-modern {
  background: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white !important;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.320, 1);
}

.close-btn-modern:hover {
  background: rgba(255, 255, 255, 0.25) !important;
  transform: rotate(90deg) scale(1.1);
}

/* Revolutionary Tabs */
.tabs-container {
  padding: 24px 32px 0;
  background: linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,1));
  backdrop-filter: blur(20px);
}

.tabs-wrapper {
  position: relative;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 6px;
  display: flex;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
}

.tab-slider {
  position: absolute;
  top: 6px;
  bottom: 6px;
  background: linear-gradient(135deg, #084468 0%, #1da1f2 50%, #3b7ca1 100%);
  border-radius: 12px;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.320, 1);
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.modern-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  min-height: 48px;
}

.modern-tab.active {
  color: white;
}

.modern-tab:not(.active) {
  color: #64748b;
}

.modern-tab:not(.active):hover {
  color: #334155;
  background: rgba(102, 126, 234, 0.05);
}

.tab-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.all-reactions-icon .icon-stack {
  position: relative;
  width: 26px;
  height: 26px;
}

.icon-stack .v-icon {
  position: absolute;
  color: currentColor;
}

.icon-1 { top: 0; left: 0; }
.icon-2 { top: 6px; left: 6px; }
.icon-3 { top: 12px; left: 12px; }

.reaction-icon-modern {
  transition: transform 0.3s ease;
}

.modern-tab:hover .reaction-icon-modern {
  transform: scale(1.1) rotate(5deg);
}

.tab-label {
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
}

.tab-count-modern {
  background: rgba(255, 255, 255, 0.9);
  color: #667eea;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 700;
  min-width: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modern-tab.active .tab-count-modern {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Content Area */
.content-area {
  padding: 32px;
  background: linear-gradient(to bottom, #ffffff, #f8fafc);
  min-height: 300px;
}

.reactions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.reaction-card-modern {
  position: relative;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.320, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.08),
    0 1px 0 rgba(255, 255, 255, 0.5) inset;
  display: flex;
  align-items: center;
  gap: 16px;
  overflow: hidden;
}

.card-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
}

.reaction-card-modern:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(102, 126, 234, 0.1);
}

.reaction-card-modern:hover .card-glow {
  opacity: 1;
}

.avatar-section {
  position: relative;
}

.avatar-container-3d {
  position: relative;
  border-radius: 50%;
  padding: 4px;
  background: linear-gradient(145deg, #f0f4f8, #d6e8f5);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.12),
    inset 0 2px 4px rgba(255, 255, 255, 0.8);
}

.user-avatar-3d {
  border-radius: 50%;
  overflow: hidden;
}

.avatar-ring {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 50%;
  background: linear-gradient(135deg, #084468 0%, #1da1f2 50%, #3b7ca1 100%);
  z-index: -1;
  animation: avatarRing 3s linear infinite;
}

@keyframes avatarRing {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.reaction-badge-floating {
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 2px solid white;
}

.badge-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.3) 0%, transparent 70%);
  animation: badgeGlow 2s ease-in-out infinite alternate;
}

@keyframes badgeGlow {
  0% { opacity: 0.3; transform: scale(0.8); }
  100% { opacity: 0.7; transform: scale(1.2); }
}

.user-section {
  flex: 1;
  min-width: 0;
}

.user-name-modern {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: linear-gradient(135deg, #1e293b, #475569);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.user-type-modern {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.type-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  animation: typeIndicator 2s ease-in-out infinite;
}

.type-agency { background: linear-gradient(45deg, #3b82f6, #1d4ed8); }
.type-volunteer { background: linear-gradient(45deg, #10b981, #047857); }

@keyframes typeIndicator {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.3); opacity: 1; }
}

.arrow-section {
  opacity: 0.4;
  transition: all 0.3s ease;
}

.arrow-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: #64748b;
}

.reaction-card-modern:hover .arrow-section {
  opacity: 1;
}

.reaction-card-modern:hover .arrow-circle {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  color: white;
  transform: translateX(4px);
}

/* Empty State */
.empty-state-modern {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 32px;
  text-align: center;
}

.empty-animation {
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 32px;
}

.empty-circle {
  position: absolute;
  border-radius: 50%;
  border: 2px solid transparent;
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%) border-box;
  mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
  mask-composite: subtract;
  animation: emptyFloat 3s ease-in-out infinite;
}

.empty-circle:nth-child(1) {
  width: 40px;
  height: 40px;
  top: 20px;
  left: 40px;
  animation-delay: 0s;
}

.empty-circle:nth-child(2) {
  width: 60px;
  height: 60px;
  top: 30px;
  left: 30px;
  animation-delay: 1s;
}

.empty-circle:nth-child(3) {
  width: 80px;
  height: 80px;
  top: 20px;
  left: 20px;
  animation-delay: 2s;
}

@keyframes emptyFloat {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #475569;
  margin-bottom: 8px;
}

.empty-subtitle {
  font-size: 1rem;
  color: #94a3b8;
  margin: 0;
}

/* Loading Animation */
.loading-modern {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 32px;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
}

.spinner-ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top: 2px solid #084468;
  animation: spinnerRing 1.5s linear infinite;
}

.spinner-ring:nth-child(3) {
  width: 40px;
  height: 40px;
  top: 20px;
  left: 20px;
  border-top-color: #084468;
  animation-delay: 1s;
}

@keyframes spinnerRing {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text-modern {
  font-size: 1.1rem;
  color: #64748b;
  font-weight: 500;
  margin: 0;
  animation: loadingPulse 2s ease-in-out infinite;
}

@keyframes loadingPulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* Modern Snackbar */
.snackbar-modern :deep(.v-snack__wrapper) {
  background: linear-gradient(135deg, #1e293b, #334155) !important;
  border-radius: 50px !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.snackbar-content {
  display: flex;
  align-items: center;
  color: white;
  font-weight: 500;
}

.snackbar-action {
  color: white !important;
  border-radius: 50% !important;
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
}

/* List Animations */
.reaction-list-enter-active {
  transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1);
}

.reaction-list-leave-active {
  transition: all 0.4s cubic-bezier(0.755, 0.05, 0.855, 0.06);
}

.reaction-list-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

.reaction-list-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.95);
}

.reaction-list-move {
  transition: transform 0.6s cubic-bezier(0.23, 1, 0.320, 1);
}

/* Responsive Design */
@media (max-width: 768px) {
  .reactions-header {
    padding: 24px 20px;
  }
  
  .header-left {
    gap: 16px;
  }
  
  .reactions-icon-container {
    width: 48px;
    height: 48px;
  }
  
  .main-icon {
    padding: 12px;
  }
  
  .title-modern {
    font-size: 1.5rem;
  }
  
  .tabs-container {
    padding: 20px 20px 0;
  }
  
  .tabs-wrapper {
    padding: 4px;
  }
  
  .modern-tab {
    padding: 10px 12px;
    gap: 6px;
    min-height: 44px;
  }
  
  .tab-label {
    font-size: 0.8rem;
  }
  
  .content-area {
    padding: 24px 20px;
  }
  
  .reactions-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .reaction-card-modern {
    padding: 16px;
    gap: 12px;
  }
  
  .user-name-modern {
    font-size: 1rem;
  }
  
  .user-type-modern {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .reactions-header {
    padding: 20px 16px;
  }
  
  .title-modern {
    font-size: 1.3rem;
  }
  
  .subtitle-modern {
    font-size: 0.85rem;
  }
  
  .tabs-container {
    padding: 16px 16px 0;
  }
  
  .modern-tab {
    padding: 8px 10px;
    gap: 4px;
    min-height: 40px;
  }
  
  .tab-label {
    font-size: 0.75rem;
  }
  
  .tab-count-modern {
    font-size: 0.7rem;
    padding: 1px 6px;
  }
  
  .content-area {
    padding: 20px 16px;
  }
  
  .reaction-card-modern {
    padding: 14px;
    gap: 10px;
  }
  
  .avatar-container-3d {
    padding: 3px;
  }
  
  .user-name-modern {
    font-size: 0.95rem;
  }
  
  .arrow-circle {
    width: 32px;
    height: 32px;
  }
}

/* Dark mode support */
/* @media (prefers-color-scheme: dark) {
  .reactions-card {
    background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
  }
  
  .tabs-wrapper {
    background: rgba(30, 41, 59, 0.8);
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .modern-tab:not(.active) {
    color: #94a3b8;
  }
  
  .modern-tab:not(.active):hover {
    color: #e2e8f0;
    background: rgba(102, 126, 234, 0.1);
  }
  
  .content-area {
    background: linear-gradient(to bottom, #1e293b, #0f172a);
  }
  
  .reaction-card-modern {
    background: rgba(30, 41, 59, 0.9);
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .user-name-modern {
    color: #e2e8f0;
  }
  
  .user-type-modern {
    color: #94a3b8;
  }
  
  .arrow-circle {
    background: linear-gradient(135deg, #334155, #1e293b);
    color: #94a3b8;
  }
  
  .empty-title {
    color: #e2e8f0;
  }
  
  .empty-subtitle {
    color: #64748b;
  }
} */

/* High-performance animations */
@supports (backdrop-filter: blur(1px)) {
  .reactions-card,
  .tabs-wrapper,
  .reaction-card-modern {
    backdrop-filter: blur(20px);
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Print styles */
@media print {
  .reactions-dialog {
    display: none !important;
  }
}
</style>